
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "@/lib/api";
import { queryKeys } from "@/lib/query-keys";
import type { ExamAccessRequest, ReviewExamRequest } from "@/lib/types";

export function useAdminUsers() {
  return useQuery({
    queryKey: queryKeys.adminUsers.list(),
    queryFn: api.getAdminUsers,
  });
}

export function useAssignExam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userID, examSlug }: { userID: string; examSlug: string }) =>
      api.assignExam(userID, examSlug),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.adminUsers.all });
      qc.invalidateQueries({ queryKey: queryKeys.exams.mine() });
    },
  });
}

export function useRevokeExam() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userID, examSlug }: { userID: string; examSlug: string }) =>
      api.revokeExam(userID, examSlug),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.adminUsers.all });
      qc.invalidateQueries({ queryKey: queryKeys.exams.mine() });
      qc.invalidateQueries({ queryKey: queryKeys.examRequests.all });
    },
  });
}

export function useMyExamRequests() {
  return useQuery({
    queryKey: queryKeys.examRequests.mine(),
    queryFn: api.getMyExamRequests,
  });
}

export function usePendingExamRequests() {
  return useQuery({
    queryKey: queryKeys.examRequests.pending(),
    queryFn: api.getPendingExamRequests,
  });
}

export function useRequestExamAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      examSlug,
      reason,
    }: {
      examSlug: string;
      reason?: string;
    }) => api.requestExamAccess(examSlug, reason ? { reason } : {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.examRequests.mine() });
    },
  });
}

export function useCancelExamAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ examSlug }: { examSlug: string }) =>
      api.cancelExamAccess(examSlug),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.examRequests.mine() });
    },
  });
}

export function useReviewExamRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userID,
      examSlug,
      data,
    }: {
      userID: string;
      examSlug: string;
      data: ReviewExamRequest;
    }) => api.reviewExamRequest(userID, examSlug, data),
    onMutate: async ({ userID, examSlug }) => {
      await qc.cancelQueries({ queryKey: queryKeys.examRequests.pending() });
      const previous = qc.getQueryData<ExamAccessRequest[]>(
        queryKeys.examRequests.pending(),
      );
      qc.setQueryData<ExamAccessRequest[]>(
        queryKeys.examRequests.pending(),
        (curr) =>
          (curr ?? []).filter(
            (r) => !(r.user_id === userID && r.exam_slug === examSlug),
          ),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(queryKeys.examRequests.pending(), ctx.previous);
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.examRequests.all });
      qc.invalidateQueries({ queryKey: queryKeys.adminUsers.all });
      qc.invalidateQueries({ queryKey: queryKeys.exams.mine() });
    },
  });
}
